import { useCallback, useEffect, useState } from 'react';

const useDataTableBulkDelete = ({
  tableRef,
  model,
  deleteFn,
  onSuccess,
  onError,
}) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [allSelected, setAllSelected] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  useEffect(() => {
    let table = null;
    let timer = null;
    let cancelled = false;
    let updateSelection = null;

    const bindIfReady = () => {
      if (cancelled) return;
      const t = tableRef?.current?.dt?.();
      if (!t) {
        timer = setTimeout(bindIfReady, 50);
        return;
      }
      table = t;

      updateSelection = () => {
        table.rows().every(function () {
          const rowNode = this.node();
          if (this.selected()) {
            rowNode.classList.add('selected');
          } else {
            rowNode.classList.remove('selected');
          }
        });

        const data = table.rows({ selected: true }).data().toArray();
        setSelectedRows(data);

        const totalOnPage = table.rows({ page: 'current' }).count();
        const selectedOnPage = table.rows({ selected: true, page: 'current' }).count();
        setAllSelected(totalOnPage > 0 && totalOnPage === selectedOnPage);
      };

      // A server-side redraw (search, filter, sort) drops rows without firing
      // deselect, so recount on draw too: only rows still ticked on screen count
      // (OL-206: Export Contacts sent rows the search had hidden).
      table.on('select deselect draw', updateSelection);
      updateSelection();
    };

    bindIfReady();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      if (table && updateSelection) {
        table.off('select deselect draw', updateSelection);
      }
    };
  }, [tableRef]);

  const toggleSelectPage = useCallback(() => {
    const table = tableRef?.current?.dt?.();
    if (!table) return;
    if (allSelected) {
      table.rows({ page: 'current' }).deselect();
      setAllSelected(false);
    } else {
      table.rows({ page: 'current' }).select();
      setAllSelected(true);
    }
  }, [allSelected, tableRef]);

  const handleBulkDelete = useCallback(async () => {
    if (!selectedRows.length) return;
    setBulkDeleteLoading(true);
    try {
      const payload = { ids: selectedRows.map((row) => row.id), model };
      const { status } = await deleteFn(payload);
      if (status) {
        const table = tableRef?.current?.dt?.();
        if (table) table.rows().deselect();
        setSelectedRows([]);
        onSuccess?.();
      }
    } catch (error) {
      onError?.(error);
    } finally {
      setBulkDeleteLoading(false);
    }
  }, [deleteFn, model, onError, onSuccess, selectedRows, tableRef]);

  const openBulkDelete = useCallback(() => {
    setBulkDeleteOpen(true);
  }, []);

  const closeBulkDelete = useCallback(() => {
    setBulkDeleteOpen(false);
  }, []);

  const confirmBulkDelete = useCallback(() => {
    setBulkDeleteOpen(false);
    handleBulkDelete();
  }, [handleBulkDelete]);

  return {
    selectedRows,
    allSelected,
    bulkDeleteLoading,
    bulkDeleteOpen,
    openBulkDelete,
    closeBulkDelete,
    confirmBulkDelete,
    toggleSelectPage,
  };
};

export default useDataTableBulkDelete;
